import React, { useState, useCallback, createContext, useContext } from 'react';
import { useAuthToken } from '@repo/hooks/useAuthToken';
import { useIdleTimeout } from '@repo/hooks/useIdleTimeout';
import { IdleWarningToast } from './IdleWarningToast';

const DEFAULT_TIMEOUT = 25 * 60 * 1000; // 25 minutes
const DEFAULT_WARNING_TIME = 30 * 1000; // 30 seconds

type IdleLogoutContextValue = {
  isWarning: boolean;
  resetTimer: () => void;
};

const IdleLogoutContext = createContext<IdleLogoutContextValue | null>(null);

export const useIdleLogout = () => {
  const context = useContext(IdleLogoutContext);
  if (!context) {
    throw new Error('useIdleLogout must be used within IdleLogoutProvider');
  }
  return context;
};

type IdleLogoutProviderProps = {
  children: React.ReactNode;
  timeout?: number;
  warningTime?: number;
  enabled?: boolean;
};

export const IdleLogoutProvider = ({
  children,
  timeout = DEFAULT_TIMEOUT,
  warningTime = DEFAULT_WARNING_TIME,
  enabled = true,
}: IdleLogoutProviderProps) => {
  const [token, setToken] = useAuthToken();
  const [isWarning, setIsWarning] = useState(false);

  const isLoggedIn = token !== null;
  const shouldTrackIdle = enabled && isLoggedIn;

  const handleWarning = useCallback(() => {
    setIsWarning(true);
  }, []);

  const handleIdle = useCallback(() => {
    setIsWarning(false);
    setToken(null);
  }, [setToken]);

  const handleActive = useCallback(() => {
    setIsWarning(false);
  }, []);

  const { resetTimer } = useIdleTimeout({
    timeout,
    warningTime,
    onWarning: handleWarning,
    onIdle: handleIdle,
    onActive: handleActive,
    enabled: shouldTrackIdle,
  });

  const contextValue: IdleLogoutContextValue = {
    isWarning,
    resetTimer,
  };

  return (
    <IdleLogoutContext.Provider value={contextValue}>
      {children}
      <IdleWarningToast open={isWarning} warningTime={warningTime} />
    </IdleLogoutContext.Provider>
  );
};

IdleLogoutProvider.displayName = 'IdleLogoutProvider';
